import React from 'react';
import { Settings, Edit, ChevronDown, Grid, Bookmark, Lock, Heart, Share2, Copy, MoreHorizontal, User, MapPin, Calendar, Link, Flame } from 'lucide-react';
import type { Post } from '../types';
import { usePosts } from '../hooks/usePosts';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface ProfileTabProps {
  icon: React.ReactNode;
  label: string;
  count?: number | string;
  isActive: boolean;
  onClick: () => void;
}

function ProfileTab({ icon, label, isActive, onClick, count }: ProfileTabProps) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center py-3 border-b-2 transition-colors ${
        isActive 
          ? 'border-blue-500 text-blue-500' 
          : 'border-transparent text-gray-400 hover:text-gray-300'
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        {count !== undefined && (
          <span className="text-xs font-medium">{count}</span>
        )}
      </div>
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

interface ProfileHeaderProps {
  isOwnProfile: boolean;
  toggleProfileType: () => void;
  username: string;
  profileImgUrl: string;
  bio: string;
  location: string;
  website: string;
  joinDate: string;
  followerCount: number;
  followingCount: number;
  likesCount: number;
  isDesktop: boolean;
}

function ProfileHeader({
  isOwnProfile,
  toggleProfileType,
  username,
  profileImgUrl,
  bio,
  location,
  website,
  joinDate,
  followerCount,
  followingCount,
  likesCount,
  isDesktop
}: ProfileHeaderProps) {
  return (
    <div className="px-4 pt-4 pb-0">
      <div className={`flex ${isDesktop ? 'items-start' : 'justify-between items-start'} mb-4`}>
        <div className={`flex ${isDesktop ? 'items-start gap-6' : 'items-center gap-4'}`}>
          <div className="relative">
            <div className={`${isDesktop ? 'w-32 h-32' : 'w-20 h-20 sm:w-24 sm:h-24'} rounded-full overflow-hidden border-2 border-blue-500`}>
              <img
                src={profileImgUrl}
                alt={username}
                className="w-full h-full object-cover"
              />
            </div>
            {isOwnProfile && (
              <button className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-1.5 shadow-lg border-2 border-black">
                <Edit className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>
          
          {isDesktop && (
            <div className="flex-1">
              <h1 className="text-white text-2xl font-bold flex items-center gap-1">
                @{username}
                {username === 'verified_user' && (
                  <span className="bg-blue-500 text-white text-xs p-0.5 rounded-full">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  </span>
                )}
              </h1>
              
              <div className="flex items-center gap-6 mt-2">
                <button className="text-white text-sm hover:underline">
                  <strong>{followingCount}</strong> <span className="text-gray-400">Following</span>
                </button>
                <button className="text-white text-sm hover:underline">
                  <strong>{followerCount}</strong> <span className="text-gray-400">Followers</span>
                </button>
                <button className="text-white text-sm hover:underline">
                  <strong>{likesCount}</strong> <span className="text-gray-400">Likes</span>
                </button>
              </div>
              
              <p className="text-white text-sm mt-4 max-w-xl">{bio}</p>
              
              <div className="flex flex-wrap gap-y-2 gap-x-4 text-gray-400 text-xs mt-3">
                {location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{location}</span>
                  </div>
                )}
                
                {website && (
                  <div className="flex items-center gap-1">
                    <Link className="w-3.5 h-3.5" />
                    <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      {website.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </div>
                )}
                
                {joinDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Joined {joinDate}</span>
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <button 
                  className="text-gray-400 text-sm flex items-center gap-1 hover:text-gray-300 transition-colors"
                  onClick={toggleProfileType}
                >
                  <span>{isOwnProfile ? 'Switch to viewing mode' : 'View as your profile'}</span>
                </button>
              </div>
            </div>
          )}
          
          {!isDesktop && (
            <div>
              <h1 className="text-white text-xl font-bold flex items-center gap-1">
                @{username}
                {username === 'verified_user' && (
                  <span className="bg-blue-500 text-white text-xs p-0.5 rounded-full">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  </span>
                )}
              </h1>
              <div className="text-gray-400 text-sm mt-1 flex items-center gap-3 flex-wrap">
                <button 
                  className="flex items-center gap-1 hover:text-gray-300 transition-colors"
                  onClick={toggleProfileType}
                >
                  <span>{isOwnProfile ? 'Switch to viewing mode' : 'View as your profile'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
        {isOwnProfile ? (
          <button className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-colors">
            <Settings className="w-4 h-4" />
            <span>Edit</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-1.5 rounded-full text-sm font-medium transition-colors">
              Follow
            </button>
            <button className="bg-gray-800 hover:bg-gray-700 text-white p-1.5 rounded-full transition-colors">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
      
      {!isDesktop && (
        <>
          <div className="mb-4">
            <p className="text-white text-sm mb-3">{bio}</p>
            
            <div className="flex flex-wrap gap-y-2 text-gray-400 text-xs">
              {location && (
                <div className="flex items-center gap-1 mr-4">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{location}</span>
                </div>
              )}
              
              {website && (
                <div className="flex items-center gap-1 mr-4">
                  <Link className="w-3.5 h-3.5" />
                  <a href={website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    {website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </div>
              )}
              
              {joinDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Joined {joinDate}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <button className="text-white text-sm hover:underline">
              <strong>{followingCount}</strong> <span className="text-gray-400">Following</span>
            </button>
            <button className="text-white text-sm hover:underline">
              <strong>{followerCount}</strong> <span className="text-gray-400">Followers</span>
            </button>
            <button className="text-white text-sm hover:underline">
              <strong>{likesCount}</strong> <span className="text-gray-400">Likes</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface VideoGridItemProps {
  post: Post;
  onClick: () => void;
}

function VideoGridItem({ post, onClick }: VideoGridItemProps) {
  return (
    <div 
      className="relative aspect-square bg-gray-900 rounded-md overflow-hidden group cursor-pointer"
      onClick={onClick}
    >
      <img
        src={post.videoUrl.replace('.mp4', '.jpg')}
        alt={post.caption}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300";
        }}
      />
      
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="p-2 bg-blue-600/90 rounded-full">
          <Flame className="w-5 h-5 text-white" />
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center gap-2 text-white text-xs">
          <div className="flex items-center gap-1">
            <Heart className="w-3 h-3 fill-white" />
            <span>{Math.floor(Math.random() * 1000) + 100}</span>
          </div>
          <div className="flex items-center gap-1">
            <Share2 className="w-3 h-3" />
            <span>{Math.floor(Math.random() * 100) + 10}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SavedVideoGridItemProps {
  post: Post;
  onClick: () => void;
}

function SavedVideoGridItem({ post, onClick }: SavedVideoGridItemProps) {
  return (
    <div 
      className="relative aspect-square bg-gray-900 rounded-md overflow-hidden group cursor-pointer"
      onClick={onClick}
    >
      <img
        src={post.videoUrl.replace('.mp4', '.jpg')}
        alt={post.caption}
        className="w-full h-full object-cover opacity-90"
        onError={(e) => {
          e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300";
        }}
      />
      
      <div className="absolute top-2 right-2">
        <div className="p-1 bg-black/60 rounded-md">
          <Bookmark className="w-4 h-4 text-white fill-white" />
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-white text-xs line-clamp-1">{post.caption}</p>
        <div className="flex items-center gap-1 mt-1">
          <img
            src={post.userAvatar}
            alt={post.username}
            className="w-3 h-3 rounded-full"
          />
          <span className="text-gray-300 text-[10px]">@{post.username}</span>
        </div>
      </div>
    </div>
  );
}

interface PrivateTabContentProps {
  onLoginRequest: () => void;
}

function PrivateTabContent({ onLoginRequest }: PrivateTabContentProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="bg-gray-800/50 p-4 rounded-full mb-4">
        <Lock className="w-12 h-12 text-gray-500" />
      </div>
      <h3 className="text-white text-lg font-semibold mb-2">This content is private</h3>
      <p className="text-gray-400 text-sm mb-4">
        You need to be following this account to see their private content
      </p>
      <button 
        onClick={onLoginRequest}
        className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
      >
        Request to Follow
      </button>
    </div>
  );
}

export function ProfileView({ onPostSelect }: { onPostSelect: (post: Post) => void }) {
  const [activeTab, setActiveTab] = React.useState('videos');
  const [isOwnProfile, setIsOwnProfile] = React.useState(true);
  const { posts } = usePosts();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  
  // Mock saved posts - just reuse the regular posts with different IDs
  const savedPosts = React.useMemo(() => {
    return posts.map(post => ({...post, id: `saved_${post.id}`}));
  }, [posts]);
  
  // Mock private posts - just reuse the regular posts with different IDs
  const privatePosts = React.useMemo(() => {
    return posts.map(post => ({...post, id: `private_${post.id}`}));
  }, [posts]);
  
  // Profile data - in a real app this would come from an API
  const profileData = {
    username: isOwnProfile ? 'your_username' : 'verified_user',
    profileImgUrl: isOwnProfile 
      ? 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=150'
      : 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150',
    bio: isOwnProfile 
      ? 'This is your profile! Edit your bio to tell the world about yourself.' 
      : 'Award-winning content creator. Sharing news and travel videos from around the world. Business inquiries: email@example.com',
    location: isOwnProfile ? 'San Francisco, CA' : 'New York, NY',
    website: isOwnProfile ? 'echo.com/me' : 'website.com',
    joinDate: 'January 2023',
    followerCount: isOwnProfile ? 245 : 1300000,
    followingCount: isOwnProfile ? 156 : 843,
    likesCount: isOwnProfile ? 1205 : 45900000,
  };
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  const toggleProfileType = () => {
    setIsOwnProfile(!isOwnProfile);
  };

  return (
    <div className={`fixed inset-0 bg-black overflow-y-auto pb-16 safe-area-bottom overscroll-y-contain ${isDesktop ? 'pt-16 ml-[240px]' : 'pt-20'}`}>
      <div className="max-w-screen-xl mx-auto">
        <ProfileHeader 
          isOwnProfile={isOwnProfile}
          toggleProfileType={toggleProfileType}
          {...profileData}
          isDesktop={isDesktop}
        />
        
        <div className="border-b border-gray-800 mb-2">
          <div className="flex">
            <ProfileTab
              icon={<Grid className="w-5 h-5" />}
              label="Videos"
              count={isOwnProfile ? posts.length : posts.length * 2}
              isActive={activeTab === 'videos'}
              onClick={() => handleTabChange('videos')}
            />
            
            <ProfileTab
              icon={<Bookmark className="w-5 h-5" />}
              label="Saved"
              count={isOwnProfile ? savedPosts.length : undefined}
              isActive={activeTab === 'saved'}
              onClick={() => handleTabChange('saved')}
            />
            
            <ProfileTab
              icon={<Lock className="w-5 h-5" />}
              label="Private"
              count={isOwnProfile ? privatePosts.length : undefined}
              isActive={activeTab === 'private'}
              onClick={() => handleTabChange('private')}
            />
          </div>
        </div>
        
        <div className="p-2">
          {activeTab === 'videos' && (
            <div className={`grid ${isDesktop ? 'grid-cols-4 md:grid-cols-5' : 'grid-cols-3'} gap-2`}>
              {posts.map((post) => (
                <VideoGridItem 
                  key={post.id}
                  post={post}
                  onClick={() => onPostSelect(post)}
                />
              ))}
              {!isOwnProfile && posts.map((post) => (
                <VideoGridItem 
                  key={`extra_${post.id}`}
                  post={post}
                  onClick={() => onPostSelect(post)}
                />
              ))}
            </div>
          )}
          
          {activeTab === 'saved' && (
            <>
              {isOwnProfile ? (
                <div className={`grid ${isDesktop ? 'grid-cols-4 md:grid-cols-5' : 'grid-cols-3'} gap-2`}>
                  {savedPosts.map((post) => (
                    <SavedVideoGridItem 
                      key={post.id}
                      post={post}
                      onClick={() => onPostSelect(post)}
                    />
                  ))}
                </div>
              ) : (
                <PrivateTabContent onLoginRequest={() => alert('Request sent!')} />
              )}
            </>
          )}
          
          {activeTab === 'private' && (
            <>
              {isOwnProfile ? (
                <div className={`grid ${isDesktop ? 'grid-cols-4 md:grid-cols-5' : 'grid-cols-3'} gap-2`}>
                  {privatePosts.map((post) => (
                    <VideoGridItem 
                      key={post.id}
                      post={post}
                      onClick={() => onPostSelect(post)}
                    />
                  ))}
                </div>
              ) : (
                <PrivateTabContent onLoginRequest={() => alert('Request sent!')} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}